import { hasPermissionFor, UserPermissionTypes } from '@nutrien/minesight-utility-module';
import dayjs from 'dayjs';
import { print } from 'graphql';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { gql } from '../../graphql/Client';
import { rootStore } from '../../mobx-models/Root';
import { BaseEntity } from '../../models/BaseEntity';
import MigrationHelper from '../MigrationHelper';
import { generatePullQuery, PullQueryContext } from '../queryBuilders/generatePullQuery';
import RxdbCollectionName from '../rxdbCollectionName';
import RxdbManager from '../RxdbManager';
import { generatePushQuery } from '../rxdbUtilityFunctions';
import { defaultDocAllTime } from '../Shared/defaultDoc';

export interface Equipment extends BaseEntity {
  readonly equipmentTypeId: string;
  readonly blockId: string;
  readonly panelId: string;
  readonly roomId: string;
  readonly shortName: string | null;
  readonly longName: string;
  readonly surveyPointId: string;
  readonly isActive: boolean;
  readonly assignedBorerEquipmentId: string | null;
  readonly comment: string | null;
  readonly equipmentStatusId: string | null;
  readonly tonnesPerScoop: number | null;
  readonly lastKnownLocationDescription: string | null;
  readonly lastLocationReportedDateTime: string | null;
  readonly modifiedOn: string | null;
}
export type EquipmentCollection = RxCollection<Equipment> | null;
export type EquipmentDocument = RxDocument<Equipment>;

export const equipmentSchema: RxJsonSchema<Equipment> = {
  type: 'object',
  version: 7,
  description: 'describes a Equipment object',
  primaryKey: 'id',
  properties: {
    id: { type: 'string', maxLength: 36 },
    equipmentTypeId: { ref: RxdbCollectionName.EQUIPMENT_TYPE, type: 'string' },
    blockId: { ref: RxdbCollectionName.BLOCKS, type: ['string', 'null'] },
    panelId: { ref: RxdbCollectionName.PANELS, type: ['string', 'null'] },
    roomId: { ref: RxdbCollectionName.ROOMS, type: ['string', 'null'] },
    surveyPointId: {
      ref: RxdbCollectionName.SURVEY_POINTS,
      type: ['string', 'null'],
    },
    shortName: { type: ['string', 'null'] },
    longName: { type: 'string' },
    isDeleted: { type: 'boolean' },
    updatedAt: { type: 'number' },
    version: { type: 'number' },
    isActive: { type: 'boolean' },
    assignedBorerEquipmentId: {
      type: ['string', 'null'],
    },
    comment: { type: ['string', 'null'] },
    equipmentStatusId: {
      ref: RxdbCollectionName.EQUIPMENT_STATUS,
      type: ['string', 'null'],
    },
    tonnesPerScoop: { type: ['number', 'null'] },
    lastKnownLocationDescription: { type: ['string', 'null'] },
    lastLocationReportedDateTime: { type: ['string', 'null'] },
    modifiedOn: { type: ['string', 'null'] },
  },
};

export const equipmentMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: EquipmentDocument) => oldDoc,
  2: (oldDoc: EquipmentDocument) => oldDoc,
  3: (oldDoc: EquipmentDocument) => ({
    ...oldDoc,
    assignedBorerEquipmentId: oldDoc.assignedBorerEquipmentId || null,
  }),
  4: (oldDoc: EquipmentDocument) => ({
    ...oldDoc,
    equipmentStatusId: null,
    comment: null,
  }),
  5: async (oldDoc: EquipmentDocument) => {
    if (!rootStore.user.siteId) throw new Error('No siteId found in rootStore.user');
    if (
      oldDoc.isActive === false &&
      !hasPermissionFor(UserPermissionTypes.EQUIPMENT_STATUS_INACTIVE_WRITE)
    ) {
      // Cannot augment data for inactive equipment without permission
      return oldDoc;
    }
    const equipmentInfo = await MigrationHelper.instance.getEquipmentDetails(oldDoc.id);
    const equipmentStatusId = equipmentInfo?.status?.id || null;
    const comment = equipmentInfo?.comment || null;
    const tonnesPerScoop = equipmentInfo?.tonnesPerScoop || null;

    return {
      ...oldDoc,
      tonnesPerScoop,
      comment,
      equipmentStatusId,
    };
  },
  6: (oldDoc: EquipmentDocument) => ({
    ...oldDoc,
  }),
  7: (oldDoc: EquipmentDocument) => ({
    ...oldDoc,
    // Can't tell if this doc has an unsynced pre-migration edit; null (-> epoch fallback on push) is
    // the safer default since treating every migrated doc as "just edited" would mass-overwrite the server.
    modifiedOn: null,
  }),
};

const equipmentFeedQuery = print(gql`
  query equipmentFeed($siteId: ID!, $lastUpdateAt: Float!, $limit: Int!) {
    equipmentFeed(lastUpdateAt: $lastUpdateAt, limit: $limit, siteId: $siteId) {
      id
      equipmentTypeId
      blockId
      panelId
      roomId
      isDeleted
      updatedAt
      version
      shortName
      longName
      surveyPointId
      isActive
      assignedBorerEquipmentId
      comment
      equipmentStatusId
      tonnesPerScoop
      lastKnownLocationDescription
      lastLocationReportedDateTime
      modifiedOn
    }
  }
`);

export const equipmentPullQueryBuilder = generatePullQuery(
  equipmentFeedQuery,
  PullQueryContext.Site,
  defaultDocAllTime,
);

export const updateEquipmentMutation = print(gql`
  mutation updateBorerEquipmentStatusComment($input: UpdateBorerEquipmentStatusCommentInput!) {
    updateBorerEquipmentStatusComment(input: $input) {
      id
      comment
      equipmentStatusId
      assignedBorerId
      version
      modifiedOn
    }
  }
`);

type UpdateBorerEquipmentStatusCommentInput = {
  id: string;
  comment: string | null;
  equipmentStatusId: string;
  assignedBorerEquipmentId: string | null;
  version: number;
  modifiedOn: string;
};

// Shape returned by updateEquipmentMutation above.
type UpdateBorerEquipmentStatusCommentResponse = {
  id: string;
  comment: string | null;
  equipmentStatusId: string | null;
  assignedBorerId: string | null;
  version: number;
  modifiedOn: string;
};

const mapEquipmentDocumentToInput = (doc: Equipment): UpdateBorerEquipmentStatusCommentInput => {
  return {
    id: doc.id,
    comment: doc.comment,
    equipmentStatusId: doc.equipmentStatusId ?? '',
    assignedBorerEquipmentId: doc.assignedBorerEquipmentId ?? null,
    version: doc.version,
    // epoch fallback (older than any real modifiedOn) so docs without a genuine edit are rejected by the server instead of echoed back as "newer"
    modifiedOn: doc.modifiedOn ?? dayjs(0).toISOString(),
  };
};

// Batch size for this push is always 1, so it's safe to remember the doc we just pushed here and
// pair it up with the response in equipmentPushResponseModifier below.
let lastPushedEquipmentDocument: Equipment | undefined;

export const equipmentPushQueryBuilder = generatePushQuery(
  updateEquipmentMutation,
  (doc: Equipment) => {
    lastPushedEquipmentDocument = doc;
    const input = mapEquipmentDocumentToInput(doc);
    return {
      input,
    };
  },
);

export const equipmentPushResponseModifier = async (
  serverDoc?: UpdateBorerEquipmentStatusCommentResponse,
) => {
  // Merge the server-authoritative version/modifiedOn/comment into the pushed doc and hand it back
  // as a "conflict", so RxDB writes it through its own conflict-resolution path. This updates the local
  // doc synchronously without re-dirtying it/re-queuing another push (unlike patching it directly), so
  // the next edit doesn't resend a stale version.
  if (serverDoc && lastPushedEquipmentDocument?.id === serverDoc.id) {
    return [
      {
        ...lastPushedEquipmentDocument,
        comment: serverDoc.comment,
        equipmentStatusId: serverDoc.equipmentStatusId,
        assignedBorerEquipmentId:
          serverDoc.assignedBorerId ?? lastPushedEquipmentDocument.assignedBorerEquipmentId,
        version: serverDoc.version,
        modifiedOn: serverDoc.modifiedOn,
      },
    ];
  }

  // Fallback for when we can't pair the response with the pushed doc: full resync instead of guessing.
  const equipmentReplicationState = RxdbManager.instance?.syncStates?.find(
    state => state.collection.name === RxdbCollectionName.EQUIPMENT,
  );
  equipmentReplicationState?.reSync();

  return [];
};
